/**
 * @file build-category-tree.ts
 *
 * Pure utility for flattening a flat list of shop categories into a
 * depth-annotated, pre-order tree sequence. Used to render the indented
 * parent-category `<select>` and to exclude a category (plus its descendants)
 * from the parent options when editing, so a category can't become its own
 * ancestor. Ported from the Website Builder v2 CategoriesTab utility; kept pure
 * so the ordering logic is unit-testable without mounting React.
 */

import type { ShopCategory } from '../api/shop-types';

/** A category flattened for display: its id, name, and nesting depth. */
export interface CategoryTreeNode {
  id: string;
  name: string;
  depth: number;
}

/**
 * Converts a flat `ShopCategory` array into a pre-order depth-annotated
 * sequence suitable for rendering an indented `<select>` option list.
 *
 * Categories are traversed recursively starting from `parentId` (default `""` =
 * root, the empty-parent sentinel categories carry now that ids are UUID strings).
 * A `visited` set guards against cycles in malformed data so the function never
 * recurses infinitely.
 *
 * @param categories - Full flat list of shop categories.
 * @param excludeId - Optional category ID to omit from the result (used when
 *   editing a category to prevent assigning it as its own parent); its whole
 *   subtree is skipped with it.
 * @param parentId - ID of the parent whose children are currently being
 *   collected; defaults to `""` (root level).
 * @param depth - Current nesting depth; incremented on each recursive call.
 * @param visited - Set of already-visited parent IDs; prevents infinite
 *   recursion on circular references.
 *
 * @returns Ordered array of `{ id, name, depth }` objects ready for rendering.
 */
export function buildCategoryTree(
  categories: ShopCategory[],
  excludeId?: string,
  parentId = '',
  depth = 0,
  visited = new Set<string>(),
): CategoryTreeNode[] {
  if (visited.has(parentId)) return [];
  visited.add(parentId);
  const result: CategoryTreeNode[] = [];
  for (const cat of categories) {
    if (cat.parent !== parentId || cat.id === excludeId) continue;
    result.push({ id: cat.id, name: cat.name, depth });
    result.push(...buildCategoryTree(categories, excludeId, cat.id, depth + 1, visited));
  }
  return result;
}
